import React, { useCallback, useEffect, useState } from "react";
import { SteeringWheelIcon } from "../../app/common/steering_wheel_icon";
import {
  hasAuthorizedWheel,
  isWheelHidSupported,
  requestWheelPermission,
} from "../../hooks/useWheelMenuNav";
import "./wheel_help_overlay.css";

// 로그인·차량선택 화면에서 게이밍 휠 조작법을 안내하는 오버레이.
// - 휠/게임패드가 연결(또는 권한 부여)되어 있을 때만 조작 도움말을 보여준다.
// - 권한이 없으면(WebHID 미페어링) "권한 요청" 버튼만 표시.
// - 휠 기능 자체가 없으면(미지원·미연결) 아무 것도 표시하지 않는다.

interface WheelHelpOverlayProps {
  // 휠/게임패드 연결 여부 — 부모(useWheelMenuNav.onConnectionChange)에서 전달.
  connected?: boolean;
  contextHint?: string;
  // 컴팩트 힌트 한 줄 문구 override — 화면별 실제 바인딩에 맞춘 안내(예: 콕핏 모니터링).
  // 미지정 시 기본(로그인/차량선택) 문구를 사용한다.
  hintText?: string;
}

const WHEEL_CONTROL_ROWS: { action: string; control: string }[] = [
  { action: "항목 이동", control: "휠 회전 · D-pad · 스틱" },
  { action: "선택", control: "가속페달 · X 버튼 · A 버튼" },
  { action: "뒤로 / 로그아웃", control: "브레이크 · ○ 버튼 · B 버튼" },
];

const WheelHelpOverlayComponent = (props: WheelHelpOverlayProps) => {
  const { connected = false, contextHint, hintText } = props;
  const [expanded, setExpanded] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const hidSupported = isWheelHidSupported();

  // 권한(페어링) 여부 확인 — 연결 상태가 바뀔 때마다 재확인.
  useEffect(() => {
    let cancelled = false;
    void hasAuthorizedWheel().then((ok) => {
      if (!cancelled) setAuthorized(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [connected]);

  const onRequest = useCallback(async () => {
    setRequesting(true);
    try {
      const ok = await requestWheelPermission();
      setAuthorized(ok);
    } finally {
      setRequesting(false);
    }
  }, []);

  // 휠로 조작 가능한 상태(연결됨 또는 권한 부여됨) → 조작 도움말 표시.
  if (connected || authorized) {
    return (
      <div className="wh-help connected">
        <button
          type="button"
          className="wh-help-hint"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label="게이밍 휠 조작 도움말 열기/닫기"
          title="게이밍 휠 조작 도움말"
        >
          <SteeringWheelIcon className="wh-help-icon" />
          <span className="wh-help-hint-text">
            {hintText ??
              "휠 회전: 항목 이동 · 가속페달/버튼: 선택 · 브레이크/버튼: 뒤로"}
          </span>
          <span className="wh-help-dot" aria-hidden="true" />
        </button>

        {expanded && (
          <div className="wh-help-panel" role="dialog" aria-label="휠 조작 안내">
            <div className="wh-help-panel-title">게이밍 휠 / 게임패드 조작</div>
            <table className="wh-help-table">
              <tbody>
                {WHEEL_CONTROL_ROWS.map((row) => (
                  <tr key={row.action}>
                    <th scope="row">{row.action}</th>
                    <td>{row.control}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {contextHint && <p className="wh-help-context">{contextHint}</p>}
            <p className="wh-help-status">
              휠/게임패드 연결됨 — 키보드 없이 조작할 수 있습니다.
            </p>
          </div>
        )}
      </div>
    );
  }

  // 권한 없음 + WebHID 지원 → 권한 요청 버튼만 표시.
  if (hidSupported) {
    return (
      <div className="wh-help">
        <button
          type="button"
          className="wh-help-hint wh-help-request"
          onClick={onRequest}
          disabled={requesting}
          title="게이밍 휠 연결 권한 요청"
        >
          <SteeringWheelIcon className="wh-help-icon" />
          <span className="wh-help-hint-text">
            {requesting ? "휠 권한 요청 중…" : "게이밍 휠 권한 요청"}
          </span>
        </button>
      </div>
    );
  }

  // 휠 기능 자체가 없으면(미지원·미연결) 아무 것도 표시하지 않음.
  return null;
};

WheelHelpOverlayComponent.displayName = "WheelHelpOverlay";

export const WheelHelpOverlay = React.memo(WheelHelpOverlayComponent);
